import React, { useEffect, useRef, useState } from 'react';

// Selector de categoría con buscador: se escribe parte del nombre, del
// código o de la categoría padre y filtra la lista (ej. "vigil" o "5.10").
// Cada opción muestra código, padre › nombre. onChange(id | '').
// Con `onNueva`, la última fila "+ Nueva categoría «texto»" llama
// onNueva(texto) (solo para quien puede crear categorías).
//
// Teclado: ↓/↑ recorren, Enter elige la resaltada, Esc descarta lo escrito.

const normalizar = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const textoCategoria = (c) =>
  c ? `${c.codigo ? `${c.codigo} ` : ''}${c.padre_nombre ? `${c.padre_nombre} › ` : ''}${c.nombre}` : '';

export default function AutocompleteCategoria({ categorias, value, onChange, onNueva, placeholder, className = '' }) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const contenedorRef = useRef(null);
  const listaRef = useRef(null);

  const elegida = categorias.find((c) => String(c.id) === String(value));

  useEffect(() => {
    setTexto(textoCategoria(elegida));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, categorias]);

  useEffect(() => {
    function alClickearFuera(e) {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setAbierto(false);
        setTexto(textoCategoria(categorias.find((c) => String(c.id) === String(value))));
      }
    }
    document.addEventListener('mousedown', alClickearFuera);
    return () => document.removeEventListener('mousedown', alClickearFuera);
  }, [categorias, value]);

  // Mientras se muestra la elegida, el desplegable lista todas; al escribir, filtra.
  const q = normalizar(texto.trim());
  const filtrando = q && texto !== textoCategoria(elegida);
  const palabras = q.split(/\s+/).filter(Boolean);
  const filtradas = filtrando
    ? categorias.filter((c) => {
        const heno = normalizar(`${c.codigo || ''} ${c.padre_nombre || ''} ${c.nombre}`);
        return palabras.every((p) => heno.includes(p));
      })
    : categorias;
  const visibles = filtradas.slice(0, 60);
  const mostrarNueva = !!onNueva;
  const total = visibles.length + (mostrarNueva ? 1 : 0);

  useEffect(() => { setResaltado(0); }, [texto]);

  useEffect(() => {
    if (!abierto || !listaRef.current) return;
    const el = listaRef.current.children[resaltado];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [resaltado, abierto]);

  function elegir(c) {
    setAbierto(false);
    setTexto(textoCategoria(c));
    onChange(c ? c.id : '');
  }

  function nueva() {
    setAbierto(false);
    setTexto(textoCategoria(elegida));
    onNueva(filtrando ? texto.trim() : '');
  }

  function alTeclear(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAbierto(true);
      setResaltado((i) => Math.min(i + 1, total - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (abierto && total > 0) {
        e.preventDefault();
        if (resaltado < visibles.length) elegir(visibles[resaltado]);
        else nueva();
      }
    } else if (e.key === 'Escape' && abierto) {
      e.preventDefault();
      e.stopPropagation();
      setAbierto(false);
      setTexto(textoCategoria(elegida));
    }
  }

  return (
    <div className={`relative ${className}`} ref={contenedorRef}>
      <input
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          setAbierto(true);
          if (!e.target.value.trim()) onChange('');
        }}
        onFocus={(e) => { setAbierto(true); e.target.select(); }}
        onKeyDown={alTeclear}
        placeholder={placeholder || 'Buscar categoría…'}
        className="border rounded-md px-2 py-1.5 w-full text-sm"
        autoComplete="off"
      />
      {abierto && (
        <ul ref={listaRef} className="absolute z-20 mt-1 w-full min-w-[16rem] bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto text-sm">
          {visibles.map((c, idx) => (
            <li
              key={c.id}
              onMouseDown={(e) => { e.preventDefault(); elegir(c); }}
              onMouseEnter={() => setResaltado(idx)}
              className={`px-3 py-1.5 cursor-pointer ${resaltado === idx ? 'bg-primary-50' : ''} ${String(c.id) === String(value) ? 'font-medium' : ''}`}
            >
              {c.codigo && <span className="mr-1.5 font-mono text-xs text-slate-400">{c.codigo}</span>}
              {c.padre_nombre && <span className="text-slate-400">{c.padre_nombre} › </span>}
              <span className="text-slate-700">{c.nombre}</span>
            </li>
          ))}
          {visibles.length === 0 && (
            <li className="px-3 py-1.5 text-slate-400">Ninguna categoría coincide.</li>
          )}
          {mostrarNueva && (
            <li
              onMouseDown={(e) => { e.preventDefault(); nueva(); }}
              onMouseEnter={() => setResaltado(visibles.length)}
              className={`px-3 py-1.5 border-t text-primary-600 cursor-pointer ${resaltado === visibles.length ? 'bg-primary-50' : ''}`}
            >
              + Nueva categoría{filtrando ? ` «${texto.trim()}»` : '…'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
